import { Alert, Button, Modal } from '@catering/ui';
import { type FormEvent, type JSX, useState } from 'react';

export interface VoidPaymentModalProps {
  submitting: boolean;
  /** Failure of the last attempt, shown here because the modal covers the page. */
  error?: string | undefined;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Same reason-required confirm pattern as `SuspendDialog`
 * (`customer-data-tab.tsx`). `payment-receipts/spec.md`: anular es
 * irreversible y restaura las facturas imputadas a su estado anterior.
 */
export function VoidPaymentModal({
  submitting,
  error,
  onCancel,
  onConfirm,
}: VoidPaymentModalProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const trimmed = reason.trim();
  const showError = touched && trimmed === '';

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setTouched(true);

    if (trimmed === '') {
      return;
    }

    onConfirm(trimmed);
  }

  return (
    <Modal open onClose={onCancel} title="Anular recibo" className="max-w-md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
        {error !== undefined && error !== '' ? <Alert tone="danger">{error}</Alert> : null}
        <p className="text-sm text-slate-600">
          Las facturas imputadas volverán a estado pendiente por el monto que este recibo les
          cubría. Esta acción no se puede deshacer.
        </p>

        <label htmlFor="void-payment-reason" className="text-sm font-medium text-slate-900">
          Motivo
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        </label>
        <textarea
          id="void-payment-reason"
          autoFocus
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-invalid={showError}
          aria-describedby={showError ? 'void-payment-reason-message' : undefined}
          className={`min-h-24 rounded-md border px-3 py-2 text-sm ${showError ? 'border-red-500' : 'border-slate-300'}`}
        />
        {showError ? (
          <p id="void-payment-reason-message" role="alert" className="text-xs text-red-600">
            El motivo es obligatorio
          </p>
        ) : null}

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" variant="destructive" loading={submitting}>
            Anular recibo
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
